import { IconButton, styled } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { DiscussionContent } from './DiscussionContent'
import { FilterOverlay } from './FilterOverlay'
import { Button } from '../../../../components/buttons'
import { CloseButton } from '../commons'
import {
  useDiscussionActionContext,
  useDiscussionStateContext,
} from '../../context/DiscussionContext'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import { FILTER_TYPES } from './filterTypes'

const TARGET_FILTER = FILTER_TYPES[0]

type DiscussionPanelProps = {
  projectUuid: string
}

export const DiscussionPanel = ({ projectUuid }: DiscussionPanelProps) => {
  const {
    isOpen,
    versionPageUuid,
    targetCandidates,
    selectedTargetUuid,
    isFilterOverlayOpen,
  } = useDiscussionStateContext()
  const { close, startNewThread, openFilterOverlay } =
    useDiscussionActionContext()

  if (!versionPageUuid || !isOpen) return null

  const isFilterAvailable = TARGET_FILTER.isAvailable(targetCandidates)
  const isFilterActive = selectedTargetUuid !== null

  return (
    <PanelContainer>
      <PanelHeader>
        <HeaderLeft>
          <Title>{intl.formatMessage({ id: 'discussion' })}</Title>
          <Button onClick={startNewThread} variant="outlined" size="s">
            {intl.formatMessage({ id: 'discussion.new' })}
          </Button>
        </HeaderLeft>
        <HeaderRight>
          {isFilterAvailable && (
            <FilterIconButton
              size="small"
              onClick={openFilterOverlay}
              aria-label={intl.formatMessage({ id: 'discussion.filter' })}
              aria-pressed={isFilterOverlayOpen}
            >
              <FilterListIcon fontSize="small" />
              {isFilterActive && <ActiveDot aria-hidden="true" />}
            </FilterIconButton>
          )}
          <CloseButton onClick={close} />
        </HeaderRight>
      </PanelHeader>
      <PanelBody>
        <DiscussionContent projectUuid={projectUuid} />
        {isFilterOverlayOpen && <FilterOverlay />}
      </PanelBody>
    </PanelContainer>
  )
}

const PanelContainer = styled('div')({
  width: '360px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const PanelHeader = styled('div')({
  padding: '16px',
  height: '56px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const Title = styled('h3')({
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
})

const FilterIconButton = styled(IconButton)({
  position: 'relative',
  color: colorPalette.monotone[7],
})

const ActiveDot = styled('span')({
  position: 'absolute',
  top: '6px',
  right: '6px',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: colorPalette.skyBlue[7],
})

const PanelBody = styled('div')({
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
})
